import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Eye, Filter, Leaf, Plus, Printer } from 'lucide-react'

import type {
    ListTeaPurchasesParams,
    TeaPurchase,
    TeaPurchasePaymentStatus,
    TeaState,
} from '@/lib/api/tea-purchase-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { RefreshButton } from '@/components/common/refresh-button'
import { MarkPaidDialog } from '@/components/tea-purchases/mark-paid-dialog'
import { TeaPurchaseFormDialog } from '@/components/tea-purchases/tea-purchase-form-dialog'
import { TeaPurchasePrintDialog } from '@/components/tea-purchases/tea-purchase-print-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useTeaPurchasesList } from '@/hooks/use-tea-purchase-query'
import { formatQty, formatVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface TeaPurchaseSearch {
    tea_state?: TeaState
    payment_status?: TeaPurchasePaymentStatus
    date_from?: string
    date_to?: string
    page?: number
}

export const Route = createFileRoute('/_app/_authed/_sidebar/tea-purchases/')({
    component: TeaPurchaseListPage,
    validateSearch: (search: Record<string, unknown>): TeaPurchaseSearch => {
        const out: TeaPurchaseSearch = {}
        if (search.tea_state === 'fresh' || search.tea_state === 'dried') {
            out.tea_state = search.tea_state
        }
        if (
            search.payment_status === 'paid' ||
            search.payment_status === 'unpaid'
        ) {
            out.payment_status = search.payment_status
        }
        if (typeof search.date_from === 'string' && search.date_from) {
            out.date_from = search.date_from
        }
        if (typeof search.date_to === 'string' && search.date_to) {
            out.date_to = search.date_to
        }
        const p =
            typeof search.page === 'number' ? search.page : Number(search.page)
        if (Number.isFinite(p) && p > 1) out.page = p
        return out
    },
})

function TeaPurchaseListPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')

    const search = Route.useSearch()
    const navigate = useNavigate({ from: '/tea-purchases' })
    const setSearch = (next: TeaPurchaseSearch) => {
        void navigate({ search: next, replace: false })
    }

    const [openCreate, setOpenCreate] = useState(false)
    const [openFilter, setOpenFilter] = useState(false)
    const [printTarget, setPrintTarget] = useState<TeaPurchase | null>(null)
    const [payTarget, setPayTarget] = useState<TeaPurchase | null>(null)

    const params = useMemo<ListTeaPurchasesParams>(
        () => ({
            page: search.page ?? 1,
            per_page: 50,
            ...(search.tea_state ? { tea_state: search.tea_state } : {}),
            ...(search.payment_status
                ? { payment_status: search.payment_status }
                : {}),
            ...(search.date_from ? { date_from: search.date_from } : {}),
            ...(search.date_to ? { date_to: search.date_to } : {}),
        }),
        [search],
    )
    const { data, isLoading, isFetching, refetch } = useTeaPurchasesList(params)
    const items = data?.data ?? []
    const total = data?.meta?.total ?? 0

    const page = search.page ?? 1

    const setPage = (p: number) =>
        setSearch({ ...search, page: p > 1 ? p : undefined })

    const filterActive =
        !!search.tea_state ||
        !!search.payment_status ||
        !!search.date_from ||
        !!search.date_to ||
        (search.page ?? 1) > 1
    const clearFilter = () => setSearch({})

    // Filter controls — reused desktop grid + mobile dialog.
    const renderFilters = () => (
        <div className="space-y-3 md:space-y-0 md:grid md:gap-3 md:grid-cols-5">
            <div>
                <Label className="mb-1 block">
                    {m.tea_purchase_tea_state()}
                </Label>
                <Select
                    value={search.tea_state ?? 'all'}
                    onValueChange={(v) =>
                        setSearch({
                            ...search,
                            tea_state:
                                v === 'all' ? undefined : (v as TeaState),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue
                            placeholder={m.tea_purchase_filter_state()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.tea_purchase_filter_state()}
                        </SelectItem>
                        <SelectItem value="fresh">
                            {m.tea_purchase_state_fresh()}
                        </SelectItem>
                        <SelectItem value="dried">
                            {m.tea_purchase_state_dried()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label className="mb-1 block">
                    {m.tea_purchase_payment_status()}
                </Label>
                <Select
                    value={search.payment_status ?? 'all'}
                    onValueChange={(v) =>
                        setSearch({
                            ...search,
                            payment_status:
                                v === 'all'
                                    ? undefined
                                    : (v as TeaPurchasePaymentStatus),
                            page: undefined,
                        })
                    }
                >
                    <SelectTrigger className="w-full">
                        <SelectValue
                            placeholder={m.tea_purchase_filter_payment()}
                        />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">
                            {m.tea_purchase_filter_payment()}
                        </SelectItem>
                        <SelectItem value="unpaid">
                            {m.tea_purchase_payment_unpaid()}
                        </SelectItem>
                        <SelectItem value="paid">
                            {m.tea_purchase_payment_paid()}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div>
                <Label className="mb-1 block">
                    {m.tea_purchase_date_from()}
                </Label>
                <Input
                    type="date"
                    value={search.date_from ?? ''}
                    onChange={(e) =>
                        setSearch({
                            ...search,
                            date_from: e.target.value || undefined,
                            page: undefined,
                        })
                    }
                />
            </div>

            <div>
                <Label className="mb-1 block">{m.tea_purchase_date_to()}</Label>
                <Input
                    type="date"
                    value={search.date_to ?? ''}
                    onChange={(e) =>
                        setSearch({
                            ...search,
                            date_to: e.target.value || undefined,
                            page: undefined,
                        })
                    }
                />
            </div>

            <div className="hidden md:flex text-right text-sm text-muted-foreground self-center justify-end">
                {m.tea_purchase_total({ count: total })}
            </div>
        </div>
    )

    return (
        <div className="space-y-4 px-1 py-2 md:p-6">
            {/* Header: mobile stack (title trên, actions dưới), desktop row */}
            <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-2">
                <div className="flex items-center gap-2">
                    <Leaf className="size-5 md:size-6 text-primary" />
                    <h1 className="text-lg md:text-2xl font-semibold">
                        {m.tea_purchase_title()}
                    </h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <RefreshButton
                        onClick={() => void refetch()}
                        loading={isFetching}
                    />
                    {/* Clear filter (luôn hiện) — đặt sát Filter button trên mobile */}
                    <ClearFilterButton
                        active={filterActive}
                        onClear={clearFilter}
                    />
                    {/* Mobile-only filter button */}
                    <Button
                        size="sm"
                        variant="outline"
                        className="md:hidden"
                        onClick={() => setOpenFilter(true)}
                    >
                        <Filter className="size-4 mr-1" />
                        {m.common_filter()}
                    </Button>
                    {canWrite && (
                        <Button onClick={() => setOpenCreate(true)}>
                            <Plus className="size-4 mr-1" />
                            {m.tea_purchase_create()}
                        </Button>
                    )}
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                {m.tea_purchase_subtitle()}
            </p>

            {/* Desktop filter grid */}
            <div className="hidden md:block">{renderFilters()}</div>

            {/* Mobile total + filter dialog */}
            <div className="md:hidden text-right text-sm text-muted-foreground">
                {m.tea_purchase_total({ count: total })}
            </div>

            <Dialog open={openFilter} onOpenChange={setOpenFilter}>
                <DialogContent className="max-w-none w-screen h-[100svh] rounded-none p-4 sm:h-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:p-6 flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{m.common_filter()}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto">
                        {renderFilters()}
                    </div>
                    <DialogFooter className="flex-row gap-2">
                        <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => {
                                clearFilter()
                                setOpenFilter(false)
                            }}
                        >
                            {m.common_clear_filter()}
                        </Button>
                        <Button
                            className="flex-1"
                            onClick={() => setOpenFilter(false)}
                        >
                            {m.common_filter_apply()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="rounded-md border bg-card overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.tea_purchase_code()}</TableHead>
                            <TableHead>{m.tea_purchase_grower()}</TableHead>
                            <TableHead>{m.tea_purchase_tea_state()}</TableHead>
                            <TableHead>{m.tea_purchase_tea_type()}</TableHead>
                            <TableHead className="text-right">
                                {m.tea_purchase_quantity()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.tea_purchase_dry_quantity()}
                            </TableHead>
                            <TableHead>
                                {m.tea_purchase_purchase_date()}
                            </TableHead>
                            <TableHead>
                                {m.tea_purchase_payment_status()}
                            </TableHead>
                            <TableHead className="text-right" />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : items.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.tea_purchase_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map((n: TeaPurchase) => (
                                <TableRow key={n.id}>
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/tea-purchases/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        {n.grower_name || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {n.tea_state === 'fresh' ? (
                                            <Badge variant="secondary">
                                                {m.tea_purchase_state_fresh()}
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline">
                                                {m.tea_purchase_state_dried()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {n.tea_type_name || '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(n.quantity)}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        <DryKgCell row={n} />
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {formatVN(n.purchase_date)}
                                    </TableCell>
                                    <TableCell>
                                        {n.payment_status === 'paid' ? (
                                            <Badge>
                                                {m.tea_purchase_payment_paid()}
                                            </Badge>
                                        ) : (
                                            <Badge variant="destructive">
                                                {m.tea_purchase_payment_unpaid()}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            {canWrite &&
                                                n.payment_status ===
                                                    'unpaid' && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() =>
                                                            setPayTarget(n)
                                                        }
                                                    >
                                                        {m.tea_purchase_action_mark_paid()}
                                                    </Button>
                                                )}
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                asChild
                                            >
                                                <Link
                                                    to="/tea-purchases/$id"
                                                    params={{ id: n.id }}
                                                >
                                                    <Eye className="size-4" />
                                                </Link>
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                title={m.tea_purchase_action_print_qr()}
                                                onClick={() =>
                                                    setPrintTarget(n)
                                                }
                                            >
                                                <Printer className="size-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {page}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <TeaPurchaseFormDialog
                open={openCreate}
                onOpenChange={setOpenCreate}
            />
            <TeaPurchasePrintDialog
                open={!!printTarget}
                onOpenChange={(v) => !v && setPrintTarget(null)}
                teaPurchase={printTarget}
            />
            <MarkPaidDialog
                open={!!payTarget}
                onOpenChange={(v) => !v && setPayTarget(null)}
                teaPurchase={payTarget}
            />
        </div>
    )
}

function DryKgCell({ row }: { row: TeaPurchase }) {
    if (row.tea_state === 'dried') {
        return <>{formatQty(row.quantity)}</>
    }
    if (row.dry_quantity != null) {
        return <>{formatQty(row.dry_quantity)}</>
    }
    return (
        <Badge variant="outline" className="font-normal">
            {m.tea_purchase_not_processed()}
        </Badge>
    )
}
